import React, { useState, useEffect, useCallback } from 'react';
import { FileText, History, Filter } from 'lucide-react';
import { Card, Button, EmptyState } from '../ui';
import { NoteCard } from './NoteCard';
import { AddNoteForm } from './AddNoteForm';
import { NoteHistoryTimeline } from './NoteHistoryTimeline';
import { workNotesApi, WorkNoteDto, NoteHistoryDto, CreateNoteRequest } from '../../services/workNotes';
import './NotesPanel.css';

type ViewMode = 'notes' | 'history';

interface NotesPanelProps {
  projectId: string;
  currentStage?: string;
  onNotesChange?: (count: number) => void;
  refreshKey?: number; // Increment to trigger a refresh
}

export const NotesPanel: React.FC<NotesPanelProps> = ({
  projectId,
  currentStage,
  onNotesChange,
  refreshKey,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('notes');
  const [notes, setNotes] = useState<WorkNoteDto[]>([]);
  const [history, setHistory] = useState<NoteHistoryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await workNotesApi.getNotes(projectId, showPinnedOnly);
      setNotes(data);
      onNotesChange?.(data.length);
    } catch (err) {
      setError('Failed to load notes');
      console.error('Error fetching notes:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, showPinnedOnly, onNotesChange]);

  const fetchHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await workNotesApi.getNoteHistory(projectId);
      setHistory(data);
    } catch (err) {
      setError('Failed to load note history');
      console.error('Error fetching history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (viewMode === 'notes') {
      fetchNotes();
    } else {
      fetchHistory();
    }
  }, [viewMode, fetchNotes, fetchHistory, refreshKey, showPinnedOnly]);

  const handleCreateNote = async (data: CreateNoteRequest) => {
    try {
      setIsSubmitting(true);
      await workNotesApi.createNote(projectId, data);
      await fetchNotes();
    } catch (err) {
      console.error('Error creating note:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await workNotesApi.deleteNote(projectId, noteId);
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const handleTogglePin = async (noteId: string) => {
    try {
      await workNotesApi.togglePin(projectId, noteId);
      await fetchNotes();
    } catch (err) {
      console.error('Error toggling pin:', err);
    }
  };

  const pinnedNotes = notes.filter(n => n.isPinned);
  const regularNotes = notes.filter(n => !n.isPinned);

  return (
    <Card className="notes-panel">
      <div className="notes-panel__header">
        <div className="notes-panel__title">
          <FileText size={18} />
          <h3>Work Notes</h3>
          {notes.length > 0 && (
            <span className="notes-panel__count">{notes.length}</span>
          )}
        </div>
        <div className="notes-panel__actions">
          <button
            className={`notes-panel__filter ${showPinnedOnly ? 'notes-panel__filter--active' : ''}`}
            onClick={() => setShowPinnedOnly(!showPinnedOnly)}
            title={showPinnedOnly ? 'Show all notes' : 'Show pinned only'}
          >
            <Filter size={14} />
          </button>
          <div className="notes-panel__view-toggle">
            <button
              className={`notes-panel__view-btn ${viewMode === 'notes' ? 'notes-panel__view-btn--active' : ''}`}
              onClick={() => setViewMode('notes')}
            >
              <FileText size={14} />
              Notes
            </button>
            <button
              className={`notes-panel__view-btn ${viewMode === 'history' ? 'notes-panel__view-btn--active' : ''}`}
              onClick={() => setViewMode('history')}
            >
              <History size={14} />
              Timeline
            </button>
          </div>
        </div>
      </div>

      <div className="notes-panel__content">
        {isLoading ? (
          <div className="notes-panel__loading">
            <div className="notes-panel__spinner" />
            <p>Loading notes...</p>
          </div>
        ) : error ? (
          <div className="notes-panel__error">
            <p>{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchNotes}>
              Try Again
            </Button>
          </div>
        ) : viewMode === 'notes' ? (
          <>
            <AddNoteForm
              onSubmit={handleCreateNote}
              isSubmitting={isSubmitting}
              compact
            />

            {notes.length === 0 ? (
              <EmptyState
                variant="no-notes"
                title={showPinnedOnly ? "No pinned notes" : "No notes yet"}
                message={showPinnedOnly ? "Pin important notes to find them quickly." : "Document your progress, ideas, and milestones as you work on this project."}
              />
            ) : (
              <div className="notes-panel__list">
                {/* Pinned notes section */}
                {pinnedNotes.length > 0 && (
                  <div className="notes-panel__pinned-section">
                    <div className="notes-panel__section-label">Pinned</div>
                    {pinnedNotes.map(note => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onEdit={setEditingNoteId}
                        onDelete={handleDeleteNote}
                        onTogglePin={handleTogglePin}
                        isEditing={editingNoteId === note.id}
                      />
                    ))}
                  </div>
                )}

                {/* Regular notes */}
                {regularNotes.length > 0 && (
                  <div className="notes-panel__regular-section">
                    {pinnedNotes.length > 0 && (
                      <div className="notes-panel__section-label">Recent</div>
                    )}
                    {regularNotes.map(note => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        onEdit={setEditingNoteId}
                        onDelete={handleDeleteNote}
                        onTogglePin={handleTogglePin}
                        isEditing={editingNoteId === note.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <NoteHistoryTimeline
            history={history}
            onTogglePin={handleTogglePin}
          />
        )}
      </div>
    </Card>
  );
};

export default NotesPanel;
